import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Search, Download, FileSpreadsheet, FileText, Filter, X } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * useTableFilter - Hook for filtering + exporting table data
 * @param {Array} data - Raw data array
 * @param {Object} config - { searchFields: string[], filterConfigs: [{key, label, options: [{value, label}]}] }
 * @returns { filtered, searchTerm, setSearchTerm, filters, setFilter, resetFilters, exportCSV, exportExcel, exportPDF, ExportBar }
 */
export function useTableFilter(data, config = {}) {
  const { searchFields = [], filterConfigs = [] } = config;
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const resetFilters = () => { setSearchTerm(""); setFilters({}); };
  const hasActiveFilters = searchTerm || Object.values(filters).some(v => v && v !== "all");

  const filtered = useMemo(() => {
    let result = [...data];
    if (searchTerm && searchFields.length > 0) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(item =>
        searchFields.some(field => {
          const val = field.split(".").reduce((o, k) => o?.[k], item);
          return val && String(val).toLowerCase().includes(lower);
        })
      );
    }
    for (const [key, value] of Object.entries(filters)) {
      if (value && value !== "all") {
        result = result.filter(item => {
          const val = key.split(".").reduce((o, k) => o?.[k], item);
          return String(val) === value;
        });
      }
    }
    return result;
  }, [data, searchTerm, filters, searchFields]);

  return { filtered, searchTerm, setSearchTerm, filters, setFilter, resetFilters, hasActiveFilters };
}

/**
 * exportCSV - Export data to CSV file
 */
export function exportCSV(data, columns, filename = "export") {
  if (!data.length) return;
  const header = columns.map(c => c.label).join(",");
  const rows = data.map(row =>
    columns.map(c => {
      let val = typeof c.accessor === "function" ? c.accessor(row) : (c.key ? c.key.split(".").reduce((o, k) => o?.[k], row) : "");
      val = val === null || val === undefined ? "" : String(val);
      if (val.includes(",") || val.includes('"') || val.includes("\n")) val = `"${val.replace(/"/g, '""')}"`;
      return val;
    }).join(",")
  );
  const csv = "\uFEFF" + [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, `${filename}.csv`);
}

/**
 * exportExcel - Export data to Excel file
 */
export function exportExcel(data, columns, filename = "export") {
  if (!data.length) return;
  const wsData = [columns.map(c => c.label)];
  data.forEach(row => {
    wsData.push(columns.map(c => {
      const val = typeof c.accessor === "function" ? c.accessor(row) : (c.key ? c.key.split(".").reduce((o, k) => o?.[k], row) : "");
      return val === null || val === undefined ? "" : val;
    }));
  });
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  // Auto-size columns
  ws["!cols"] = columns.map((_, i) => ({
    wch: Math.max(columns[i].label.length, ...data.slice(0, 50).map(row => {
      const val = typeof columns[i].accessor === "function" ? columns[i].accessor(row) : (columns[i].key ? columns[i].key.split(".").reduce((o, k) => o?.[k], row) : "");
      return String(val || "").length;
    })) + 2
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Donnees");
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

/**
 * exportPDF - Export data to PDF file
 */
export function exportPDF(data, columns, filename = "export", title = "") {
  if (!data.length) return;
  const doc = new jsPDF({ orientation: columns.length > 5 ? "landscape" : "portrait" });
  if (title) {
    doc.setFontSize(14);
    doc.text(title, 14, 15);
  }
  const headers = columns.map(c => c.label);
  const body = data.map(row =>
    columns.map(c => {
      const val = typeof c.accessor === "function" ? c.accessor(row) : (c.key ? c.key.split(".").reduce((o, k) => o?.[k], row) : "");
      return val === null || val === undefined ? "" : String(val);
    })
  );
  doc.autoTable({
    head: [headers],
    body,
    startY: title ? 22 : 14,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [15, 82, 186], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 10, right: 10 },
  });
  doc.save(`${filename}.pdf`);
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

/**
 * ExportBar - Reusable toolbar with search, filters, and export buttons
 */
export function ExportBar({
  searchTerm, onSearchChange, searchPlaceholder = "Rechercher...",
  filterConfigs = [], filters = {}, onFilterChange,
  onReset, hasActiveFilters,
  data, columns, filename = "export", title = "",
  children
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="export-bar">
      {searchPlaceholder && onSearchChange && (
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm || ""}
            onChange={e => onSearchChange(e.target.value)}
            className="pl-9 w-[220px]"
            data-testid="export-bar-search"
          />
        </div>
      )}
      {filterConfigs.map(fc => (
        <Select key={fc.key} value={filters[fc.key] || "all"} onValueChange={v => onFilterChange(fc.key, v)}>
          <SelectTrigger className="w-[180px]" data-testid={`filter-${fc.key}`}>
            <SelectValue placeholder={fc.label} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{fc.label}</SelectItem>
            {fc.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ))}
      {hasActiveFilters && (
        <Button variant="outline" size="sm" onClick={onReset} data-testid="btn-reset-filters">
          <X className="w-4 h-4 mr-1" />Reinitialiser
        </Button>
      )}
      {children}
      <div className="ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="btn-export">
              <Download className="w-4 h-4 mr-2" />Exporter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportCSV(data, columns, filename)} data-testid="export-csv">
              <FileText className="w-4 h-4 mr-2" />CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportExcel(data, columns, filename)} data-testid="export-excel">
              <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportPDF(data, columns, filename, title)} data-testid="export-pdf">
              <FileText className="w-4 h-4 mr-2" />PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
